import { OpenInWindow } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useQuery } from 'urql'
import { KeyValueDrawer } from '../activities/KeyValueDrawer'
import { ContactNameIcon } from '../contacts/ContactNameIcon'
import Tag from '../tags/Tag'
import Spinner from 'app/components/Spinner'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { graphql } from 'app/generated/gql'
import type { ActivityContact } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { useAuthStore } from 'app/stores/StoreContext'

const CONTACT_QUERY = graphql(`
  query ContactCard($id: ID!) {
    contact(id: $id) {
      id
      name
      birthDate
      becameLeadDate
      becamePatientDate
      cellPhone
      leadStatus
      lifecycleStage
      matchTypes
      patientStatus
      tags
      workspaceId
    }
  }
`)

type Props = {
  activityContact: Pick<ActivityContact, 'id' | 'name'>
  isIntegrated: boolean
  onContactClick?: (contactId: string) => void
}

// observer: reads authStore.products in render (agentOnly gating). products is reassigned
// in place on a workspace switch, so without observer the card would not react (TRK-6020).
export const ContactCard = observer(function ContactCard({ activityContact, isIntegrated, onContactClick }: Props) {
  const user = useUser()
  const { products } = useAuthStore()
  if (!user) return null
  const agentOnly = products?.includes(`AI Agent`) && !products?.includes(`CRM`)

  const queryContext = useMemo(() => ({ suppressErrorToast: true }), [])
  const [{ data, fetching, error }] = useQuery({
    query: CONTACT_QUERY,
    variables: { id: activityContact.id },
    context: queryContext,
  })

  const contact = data?.contact

  const getTags = () =>
    contact?.tags?.map((tag) => (
      <Tag
        key={tag}
        workspaceId={contact?.workspaceId}
        model="contacts"
        tag={tag}
        removeTag={() => {}}
        tagClassName="text-ui-xs leading-4"
        readOnly
      />
    ))

  const getLifecycle = () => {
    const lifecycleStage = contact?.lifecycleStage
    return (
      <span className="mt-0.5 text-ui-xs text-green-600">
        {contact?.patientStatus === `NonPatient` && lifecycleStage === `Patient` ? `` : lifecycleStage}
      </span>
    )
  }

  return (
    <div className="mb-4 p-4">
      {fetching ? (
        <div className="flex items-center justify-center">
          <Spinner size={10} />
        </div>
      ) : (
        <>
          {contact && (
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center justify-between bg-surface-subtle">
                <div className="flex items-center gap-x-2">
                  <ContactNameIcon
                    contact={contact as unknown as ContactJson}
                    heightClassName="h-5"
                    widthClassName="w-5"
                    isTiny
                  />
                  <span className="text-ui-sm text-strong">{contact?.name}</span>
                </div>
                {agentOnly ? (
                  <button type="button" onClick={() => onContactClick?.(contact.id!)} className="hover:opacity-80">
                    <OpenInWindow width={17} height={18} className="cursor-pointer" />
                  </button>
                ) : (
                  <a
                    href={`/contacts?drawer=${contact?.id}&wId=${contact?.workspaceId || ``}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline hover:no-underline"
                  >
                    <OpenInWindow width={17} height={18} className="cursor-pointer" />
                  </a>
                )}
              </div>
              <div className="flex gap-x-1 bg-surface-subtle">{getTags()}</div>
              <div className="rounded-lg border bg-surface px-4 py-3 shadow-sm">
                <KeyValueDrawer
                  map={{
                    Lifecycle: getLifecycle(),
                    'Lead Status': contact?.leadStatus || `-`,
                    'Phone Number': formatPhoneNumber(contact?.cellPhone ?? undefined) || `-`,
                    'Birth Date': formatDate({ value: contact?.birthDate, dateOnly: true }) || `-`,
                    'Became Lead Date': formatDate({ value: contact?.becameLeadDate, dateOnly: true }) || `-`,
                    'Became Patient Date': formatDate({ value: contact?.becamePatientDate, dateOnly: true }) || `-`,
                  }}
                  className={{ 'Birth Date': `phi` }}
                />
                {user.type === `super` && (
                  <div className="mt-3 flex w-full flex-col text-body-xs">
                    <span className="text-subtle">Match Types</span>
                    <div className="mt-0.5 text-ui-xs text-strong">{contact?.matchTypes?.join(`, `) || `-`}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {error && <div className="text-ui-sm text-strong">{error.message}</div>}
        </>
      )}
    </div>
  )
})
